import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useSearchParams } from "react-router-dom";

// Keeps a filter value in the URL so links can be shared and survive refresh
export default function useQueryState<T extends string>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = (searchParams.get(key) as T | null) ?? defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue || next === "") params.delete(key);
          else params.set(key, next);
          // Any filter change sends the list back to page 1
          if (key !== "page") params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
}

export function usePageParam(): [number, (page: number) => void] {
  const [raw, setRaw] = useQueryState<string>("page", "1");
  const page = Math.max(1, Number(raw) || 1);
  const setPage = useCallback((next: number) => setRaw(String(next)), [setRaw]);
  return [page, setPage];
}

// Text input that writes to a URL param after the user stops typing
export function useDebouncedQueryState(
  key: string,
  delay = 400,
): [string, string, (value: string) => void] {
  const [query, setQuery] = useQueryState<string>(key, "");
  const [input, setInput] = useState(query);
  const [debounced] = useDebounceValue(input, delay);
  const lastPushed = useRef(debounced);

  useEffect(() => {
    if (debounced === lastPushed.current) return;
    lastPushed.current = debounced;
    setQuery(debounced);
  }, [debounced, setQuery]);

  // URL changed elsewhere (clear filters, another platform): mirror it in the box
  useEffect(() => {
    setInput((prev) => (prev === query ? prev : query));
    lastPushed.current = query;
  }, [query]);

  return [query, input, setInput];
}

// ----------------------- COMMA LIST IN ONE URL PARAM ------------------------------------
export function useListQueryState(key: string): [string[], (values: string[]) => void] {
  const [raw, setRaw] = useQueryState<string>(key, "");
  const values = raw ? raw.split(",") : [];
  const setValues = useCallback((next: string[]) => setRaw(next.join(",")), [setRaw]);
  return [values, setValues];
}
